import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, NavigationEnd, PRIMARY_OUTLET, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FaqArticleDto } from '@models/faq';
import { NewsArticleDto } from '@models/news-article';
import { TranslatePipe } from '@ngx-translate/core';

export interface BreadcrumbItem {
  label: string;
  url: string;
  isStatic: boolean;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.html',
  styleUrl: './breadcrumb.scss',
  imports: [RouterLink, TranslatePipe]
})
export class Breadcrumb {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  public readonly breadcrumbs = signal<BreadcrumbItem[]>([]);

  constructor() {
    this.setBreadcrumbs();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.setBreadcrumbs();
      });
  }

  tryGetTitle(data: Data): string {
    const pageData: FaqArticleDto | NewsArticleDto | null = data?.['pageData'];
    return pageData?.Title ?? '';
  }

  private setBreadcrumbs(): void {
    const root: ActivatedRoute = this.route.root;
    const homeBreadcrumb: BreadcrumbItem = {
      label: 'HOME',
      url: '',
      isStatic: true
    };

    this.breadcrumbs.set([homeBreadcrumb, ...this.getBreadcrumbs(root)]);
  }

  private getBreadcrumbs(route: ActivatedRoute, url = '', breadcrumbs: BreadcrumbItem[] = []): BreadcrumbItem[] {
    const ROUTE_DATA_BREADCRUMB = 'breadcrumb';

    //get the primary child route
    const child: ActivatedRoute | undefined = route.children.find(c => c.outlet === PRIMARY_OUTLET);
    //return if there are no more children
    if (!child) {
      return breadcrumbs;
    }

    //append the route's URL segments to URL; empty-path routes, such as the root of a lazy-loaded section, add none
    const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
    if (routeURL) {
      url += `/${routeURL}`;
    }

    //read the route's own breadcrumb, since empty-path routes inherit their parent's data
    const staticLabel: string | undefined = child.routeConfig?.data?.[ROUTE_DATA_BREADCRUMB];
    const label: string = staticLabel ?? this.tryGetTitle(child.snapshot.data);

    //add one breadcrumb per URL, skipping Home and routes without a label
    if (label && url && url !== breadcrumbs.at(-1)?.url) {
      breadcrumbs.push({
        label,
        url,
        isStatic: !!staticLabel
      });
    }

    //recursive
    return this.getBreadcrumbs(child, url, breadcrumbs);
  }
}
